import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

import { getLeadSummary } from '@/api/lead_summary'
import { dateFnsLocale } from '@/utils/dateLocale'

/**
 * Carlink AI — блок AI-сводки на карточке лида (TL;DR + флаги + следующий шаг).
 *
 * Автогенерация при открытии карточки, кэш по fingerprint на бэке (react-query
 * держит результат staleTime=5мин). Кнопка «обновить» форсит пересборку.
 *
 * Рамка «бегущий огонёк» (вариант А): бежит пока генерится → пара прогонов →
 * плавно гаснет в статичную оранжевую обводку. Классы и keyframes — в theme.css
 * (`.carlink-ai-frame`). prefers-reduced-motion уважается там же.
 */
export function CarlinkAiSummary({ leadId }: { leadId: string }) {
  const { t } = useTranslation()
  // force прокидываем через ref — refetch перечитывает свежим, кнопка ставит флаг.
  const forceRef = useRef(false)
  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['lead-ai-summary', leadId],
    queryFn: () => {
      const f = forceRef.current
      forceRef.current = false
      return getLeadSummary(leadId, f)
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  })

  const reduceMotion = useRef(false)
  useEffect(() => {
    reduceMotion.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  }, [])

  const [phase, setPhase] = useState<'running' | 'settling' | 'done'>('running')
  useEffect(() => {
    if (isFetching) setPhase('running')
    // При reduced-motion анимации нет → animationend не придёт, осаживаем сразу.
    else setPhase((p) => (p === 'running' ? (reduceMotion.current ? 'done' : 'settling') : p))
  }, [isFetching])

  // Подстраховка: если animationend по ::before не сработает — всё равно осядем
  // в статичную оранжевую рамку (оранжевый появляется после пробега).
  useEffect(() => {
    if (phase !== 'settling') return
    const t = setTimeout(() => setPhase('done'), 3500)
    return () => clearTimeout(t)
  }, [phase])

  const phaseCls =
    phase === 'running' ? 'is-running' : phase === 'settling' ? 'is-settling' : 'is-done'

  function handleRefresh() {
    if (isFetching) return
    forceRef.current = true
    void refetch()
  }

  return (
    <div
      className={`carlink-ai-frame ${phaseCls}`}
      onAnimationEnd={() => setPhase((p) => (p === 'settling' ? 'done' : p))}
    >
      <div className="carlink-ai-inner px-4 py-3.5">
        <div className="mb-2.5 flex items-center gap-2 border-b border-[color:var(--stryzh-color-border-subtle)] pb-2">
          <span className="h-2 w-2 rounded-full bg-[color:var(--stryzh-color-accent-orange)]" />
          <span className="text-xs font-semibold text-[color:var(--stryzh-color-text-primary)]">
            Carlink AI
          </span>
          {data ? (
            <span className="text-[11px] text-[color:var(--stryzh-color-text-secondary)]">
              {t('leads.ai.updated')}{' '}
              {formatDistanceToNow(new Date(data.generated_at), { locale: dateFnsLocale(), addSuffix: true })}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            title={t('leads.ai.refreshTitle')}
            aria-label={t('leads.ai.refreshTitle')}
            className="ml-auto text-[color:var(--stryzh-color-text-secondary)] transition hover:text-[color:var(--stryzh-color-text-primary)] disabled:opacity-40"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isFetching ? 'animate-spin' : ''}
            >
              <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
          </button>
        </div>

        {isError ? (
          <div className="text-sm text-red-200">
            {t('leads.ai.error', { msg: (error as Error)?.message ?? t('leads.ai.errorFallback') })}{' '}
            <button type="button" onClick={handleRefresh} className="underline">
              {t('leads.ai.retry')}
            </button>
          </div>
        ) : !data ? (
          <div className="animate-pulse text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.ai.loading')}
          </div>
        ) : (
          <>
            <p className="mb-2.5 text-sm leading-relaxed text-[color:var(--stryzh-color-text-primary)]">
              {data.summary.tldr}
            </p>

            {data.summary.open?.length ? (
              <div className="mb-2.5 text-[13px] leading-snug text-[color:var(--stryzh-color-text-secondary)]">
                <span className="font-semibold text-[color:var(--stryzh-color-text-primary)]">{t('leads.ai.open')}</span>
                <ul className="ml-4 mt-1 list-disc">
                  {data.summary.open.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            ) : null}

            {data.summary.next_step ? (
              <div className="flex items-start gap-1.5 text-[13px] leading-snug text-[color:var(--stryzh-color-text-secondary)]">
                <span className="mt-px text-[color:var(--stryzh-color-accent-orange)]">→</span>
                <span>
                  <span className="font-semibold text-[color:var(--stryzh-color-text-primary)]">
                    {t('leads.ai.nextStep')}
                  </span>{' '}
                  {data.summary.next_step}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
